<template>
	<component
		:is="component.component"
		v-bind="component.props"
		class="asset-element-card-vertical module module--on-white-bg"
	>
		<div class="position-relative">
			<span
				:class="[
					'img-lazy-wrapper top-border-radius',
					isRealEstateOrBond ? 'aspect-ratio-4-3' : 'aspect-ratio-16-9'
				]"
			>
				<span class="loader alpha"></span>
				<span class="alias fade top-border-radius" :src="image"></span>
				<img
					class="top-border-radius"
					style="opacity: 1; object-fit: cover"
					:src="image"
					alt=""
					role="presentation"
				/>
			</span>
		</div>
		<div class="asset-element-card-vertical__content">
			<div>
				<h4 class="subheading">{{ assetName }}</h4>

				<p v-if="assetLocation" class="body-sm mt-0">{{ assetLocation }}</p>
				<p v-else-if="assetTagline" class="body-sm mt-0">{{ assetTagline }}</p>
			</div>
			<Tag v-if="assetType" :text="assetType" size="xs" class="align-self-start" />
		</div>
	</component>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { Asset } from 'types/assets';
import BaseLink from '@components/_base-link.vue';
import { DynamicComponent } from 'types/vue';
import { Link } from 'types/layout';
import Tag from '@components/tags/tag-component.vue';
import { useAppStore } from '@stores/app';
import type { Component as ComponentType } from 'vue';

interface Props {
	assetElement: Asset;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const readOnlyAsset = inject('readOnlyAsset', ref(false));

const component = computed((): DynamicComponent => {
	if (readOnlyAsset.value) {
		return {
			component: 'div' as unknown as ComponentType
		};
	} else {
		return {
			component: BaseLink,
			props: {
				link: routerLink.value,
				classes: 'hover'
			}
		};
	}
});

const isRealEstateOrBond = computed(
	(): boolean => props.assetElement.elementType === 'REAL_ESTATE' || props.assetElement.elementType === 'BOND'
);

const image = computed((): string => {
	if (isRealEstateOrBond.value) {
		return props.assetElement.thumbnail?.url ?? '';
	} else {
		return props.assetElement.ventureCapitalFields?.marketingImage?.url ?? props.assetElement.thumbnail?.url ?? '';
	}
});

const assetName = computed((): string => props.assetElement.name ?? props.assetElement.assetType);

const assetLocation = computed((): string => {
	if (isRealEstateOrBond.value) {
		return props.assetElement.realEstateAndPrivateCreditFields?.city &&
			props.assetElement.realEstateAndPrivateCreditFields.state
			? `${props.assetElement.realEstateAndPrivateCreditFields.city}, ${props.assetElement.realEstateAndPrivateCreditFields.state}`
			: '';
	} else {
		return '';
	}
});

const assetTagline = computed((): string => {
	return props.assetElement.ventureCapitalFields?.tagline ?? '';
});

const assetType = computed((): string => (isRealEstateOrBond.value ? props.assetElement.assetType.name : ''));

const routerLink = computed((): Link => {
	switch (props.assetElement.elementType) {
		case 'REAL_ESTATE': {
			return {
				router: 'real-estate-assets',
				params: { reitElementId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'VENTURE': {
			return {
				router: 'company-assets',
				params: { companyId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'VENTURE_FUND': {
			return {
				router: 'venture-fund-assets',
				params: { ventureFundId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'BOND': {
			return {
				router: 'bond-assets',
				params: { bondId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		default:
			return { router: '404', forceHrefLink: appStore.isMobileWebview };
	}
});
</script>
<style lang="scss">
@use '../../styles/constants/_colors.scss' as *;

$asset-element-card-vertical-border-radius: 0.5rem;
$asset-element-card-vertical-border-width: 1px;

.asset-element-card-vertical {
	color: token('content-primary');
	border-radius: $asset-element-card-vertical-border-radius;
	margin-top: 0 !important;
	display: flex;
	flex-direction: column;

	&.hover:hover {
		color: token('content-primary');
		background: token('bg-secondary');
		border: $asset-element-card-vertical-border-width solid token('content-primary');
		cursor: pointer;
	}

	&__content {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: 1rem;
		flex-grow: 1;
		padding: 1rem;
	}
}

.top-border-radius {
	border-top-left-radius: calc(
		$asset-element-card-vertical-border-radius - $asset-element-card-vertical-border-width
	);
	border-top-right-radius: calc(
		$asset-element-card-vertical-border-radius - $asset-element-card-vertical-border-width
	);
}
</style>
